// src/merch/OrderStatus.jsx
//
// /order/:id?t=<access token> — the customer's order page (linked from
// Stripe's success redirect and every email). No account needed.

import { StoreLogo } from "./StoreBrand.jsx";
import React, { useEffect, useState } from "react";
import { callFn } from "./supabase";
import { clearSavedDesign } from "./persist";

const STEPS = [
  { id: "awaiting_review", label: "In review", blurb: "We're checking your design. Your card is only authorized — no charge yet." },
  { id: "approved", label: "Approved", blurb: "Looks great! Heading to the printer." },
  { id: "printed", label: "Printed", blurb: "Printed, cut and being packed." },
  { id: "shipped", label: "Shipped", blurb: "On its way to you." },
];

export default function OrderStatus({ orderId, token, justPaid, homeHref }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (justPaid) clearSavedDesign(); // order placed — start fresh next time
    let alive = true;
    const load = () =>
      callFn("merch-order-status", { orderId, accessToken: token })
        .then((d) => alive && setData(d))
        .catch((e) => alive && setError(e.message));
    load();
    // right after checkout the webhook may lag a few seconds
    const t = justPaid ? setInterval(load, 4000) : null;
    const stop = setTimeout(() => t && clearInterval(t), 40000);
    return () => { alive = false; t && clearInterval(t); clearTimeout(stop); };
  }, [orderId, token, justPaid]);

  const o = data?.order;
  const ref = orderId.slice(0, 8).toUpperCase();
  const idx = o ? STEPS.findIndex((s) => s.id === o.status) : -1;

  return (
    <div className="ma-app">
      <header className="ma-header">
        <StoreLogo link={homeHref} />
        <div className="ma-sub">Order {ref}</div>
      </header>
      <main className="ma-status">
        {error && <div className="ma-note error">{error}</div>}
        {!o && !error && <p className="ma-muted">Loading your order…</p>}
        {o && (
          <div className="ma-panel">
            {o.status === "pending_payment" && (
              <p>{justPaid ? "Confirming your payment…" : "This order hasn't been paid yet."}</p>
            )}
            {o.status === "rejected" && (
              <>
                <h2>We couldn't print this design</h2>
                <div className="ma-note warn">{o.reject_reason}</div>
                <p>You haven't been charged — the hold on your card has been released.</p>
                <a className="ma-btn primary" href={homeHref}>Make a new design</a>
              </>
            )}
            {(o.status === "expired" || o.status === "abandoned") && (
              <p>This order is no longer active and you haven't been charged. Questions? Just reply to any of our emails.</p>
            )}
            {idx >= 0 && (
              <>
                <h2>{justPaid && idx === 0 ? "Thanks — order received! 🏒" : STEPS[idx].label}</h2>
                <ol className="ma-steps">
                  {STEPS.map((s, i) => (
                    <li key={s.id} className={i < idx ? "done" : i === idx ? "now" : ""}>
                      <b>{s.label}</b>
                      {i === idx && <span>{s.blurb}</span>}
                    </li>
                  ))}
                </ol>
                {o.tracking_number && <p>Tracking: <b>{o.tracking_number}</b></p>}
              </>
            )}
            {data.preview && <img className="ma-status-preview" src={data.preview} alt="Your design" />}
            <p className="ma-muted">
              {o.quantity} × {o.product_id} · {o.size_id} · {o.colour_id} · {o.sides.join(" + ")}
              {o.total_cents != null && ` · $${(o.total_cents / 100).toFixed(2)}`}
            </p>
            <a className="ma-btn ghost" href={homeHref}>Design another</a>
          </div>
        )}
      </main>
    </div>
  );
}
